import * as echarts from "echarts";
import { EChartsType } from "echarts";
import { ECBasicOption } from "echarts/types/dist/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { datetime } from "../../../util/library/datetime";
import { SensorGroupType, SHORT_DATETIME_FORMAT } from "../../../util/varibles/constants";
import { firstLetter } from "../../../util/varibles/global";
import { ISensorItem, ITankProcessData } from "../../../util/varibles/interface";
import styles from "../style.module.scss";
import { sensorConfig, sensorSeriesRenderers, UNIT_AXES } from "./contants";
import { useProcessData } from "./ProcessDataContext";

const convertData = (items: any[], start?: number, finish?: number) => {
    const result =
        start && finish
            ? items.filter(item => {
                const time = new Date(item.timestamp).getTime();

                if (time < start) return false;
                if (time > finish) return false;

                return true;
            })
            : [];

    return result.map(item => [
        new Date(item.timestamp).getTime(),
        Number(item.value.toFixed(2)),
    ]);
};


const formatTime = (
    value: number,
    showDate: boolean
) => {
    const date = new Date(value);
    const pad = (number: number) => `${number}`.padStart(2, "0");

    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

    if (!showDate) return time;

    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}, ${time}`;
};

const formatTooltipTime = (value: number) => {
    const date = new Date(value);
    const pad = (number: number) => `${number}`.padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getLegendColor = (type: SensorGroupType, name: string) => {
    if (type === "cargoPressure") {
        const isTop = name.toLowerCase().includes("top")
        return isTop ? "#F2597F" : "#0770FF";
    }

    return sensorConfig[type].color || "#5470c6";
};

const tooltipMarker = (sensorType: SensorGroupType, seriesName = "") => {
    const { color, symbol, lineType, itemStyle } = sensorConfig[sensorType] || {};
    const isCargo = sensorType === "cargoPressure";
    const isTop = seriesName.toLowerCase().includes("top");
    const markerColor = getLegendColor(sensorType, seriesName);

    const dashed = lineType === "dashed" ? "border-top-style: dashed;" : "";
    const base =
        "display:inline-block;position:relative;width:24px;height:12px;margin-right:8px;vertical-align:-2px;";
    const line = `<span style="position:absolute;left:0;right:0;top:6px;border-top:2px solid ${markerColor};${dashed}"></span>`;

    if (isCargo) {
        const area = isTop
            ? "linear-gradient(180deg, rgba(213,72,120,0.55), rgba(213,72,120,0.18))"
            : "linear-gradient(180deg, rgba(58,77,233,0.55), rgba(58,77,233,0.18))";

        return `<span style="display:inline-block;position:relative;width:28px;height:16px;margin-right:8px;vertical-align:-4px;">
            <span style="position:absolute;left:0;right:0;top:5px;bottom:0;background:${area};border-radius:1px;"></span>
            <span style="position:absolute;left:0;right:0;top:4px;border-top:2px solid ${markerColor};"></span>
            <span style="position:absolute;left:10px;top:1px;width:8px;height:8px;border-radius:50%;background:${markerColor};"></span>
        </span>`;
    }

    if (symbol === "none") return `<span style="${base}">${line}</span>`;

    if (symbol === "triangle") {
        const fill = itemStyle?.color || color;
        const border = itemStyle?.borderColor || color;

        return `<span style="${base}">${line}<span style="position:absolute;left:8px;top:1px;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:9px solid ${border};"></span><span style="position:absolute;left:10px;top:4px;width:0;height:0;border-left:3px solid transparent;border-right:3px solid transparent;border-bottom:5px solid ${fill};"></span></span>`;
    }

    const fill = itemStyle?.color || color;
    const border = itemStyle?.borderColor || color;
    const borderWidth = itemStyle?.borderWidth || 1;

    return `<span style="${base}">${line}<span style="position:absolute;left:8px;top:2px;width:8px;height:8px;border-radius:50%;box-sizing:border-box;background:${fill};border:${borderWidth}px solid ${border};"></span></span>`;
};

const LegendMarker: React.FC<{ sensor: ISensorItem }> = ({ sensor }) => {
    const color = getLegendColor(sensor.type, sensor.name);
    const config = sensorConfig[sensor.type];

    if (sensor.type === "temperature") {
        return (
            <span
                className={styles.legendMarker}
                data-type="dash"
                style={{ color }}
            />
        );
    }

    if (sensor.type === "co2") {
        return (
            <span className={styles.legendMarker} data-type="triangle">
                <span className={styles.triangleOuter} />
                <span className={styles.triangleInner} />
            </span>
        );
    }

    if (sensor.type === "cargoPressure") {
        return (
            <span
                className={styles.legendMarker}
                data-type="area"
                data-cargo={sensor.name.includes("top") ? "top" : "bottom"}
                style={{ color }}
            >
                <span className={styles.cargoBubble} />
            </span>
        );
    }

    if (config.symbol === "none") {
        return (
            <span
                className={styles.legendMarker}
                data-type="line"
                style={{ color }}
            />
        );
    }

    return (
        <span
            className={styles.legendMarker}
            data-type="circle"
            style={{ color }}
        />
    );
};

const unitAxis = ({ id, name, color, offset = 0, showSplitLine = false }: { id: string; name: string; color: string; offset?: number; showSplitLine?: boolean; }) => {
    return {
        id,
        type: "value",
        name,
        position: "left",
        offset,
        splitNumber: 5,
        alignTicks: true,
        axisLine: { show: false },
        axisTick: { show: false },
        nameGap: 12,
        nameTextStyle: {
            color,
            fontSize: 12,
            align: "right",
            padding: [10, 0],
        },
        axisLabel: {
            color,
            fontSize: 12,
            margin: 0,
            formatter: (value: number) => Number.isInteger(value) ? `${value}` : `${Number(value.toFixed(2))}`,
        },
        splitLine: {
            show: showSplitLine,
            lineStyle: {
                color: "#dce4ef",
            },
        },
    }
};

export default function TankChart({ data, name, showDateOnXAxis, }: { data: ITankProcessData; name: string; showDateOnXAxis: boolean }) {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<EChartsType | null>(null);

    const [start] = useProcessData(state => state.start);
    const [finish] = useProcessData(state => state.finish);

    const sensors = useMemo(() => {
        return Object.keys(data).flatMap(key =>
            data[key].map(sub => {
                const type = sub.type.replace(/_/g, "") as SensorGroupType;
                const { yAxisId } = sensorConfig[type] || {};
                const unit = sub.unit?.toLowerCase() || UNIT_AXES.find(item => item.id === yAxisId)?.name || "";

                return { ...sub, type, unit };
            })
        );
    }, [data]);

    const [selectedSeries, setSelectedSeries] = useState<Record<string, boolean>>({});
    const prevRange = useRef({ start, finish });

    useEffect(() => {
        if (!chartInstance.current) return;

        if (
            prevRange.current.start !== start ||
            prevRange.current.finish !== finish
        ) {
            chartInstance.current.dispatchAction({
                type: "dataZoom",
                start: 0,
                end: 100,
            });

            prevRange.current = { start, finish };
        }
    }, [start, finish]);

    useEffect(() => {
        setSelectedSeries(prev => {
            const next: Record<string, boolean> = {};
            sensors.forEach(sensor => { next[sensor.name] = prev[sensor.name] ?? true; });

            return next;
        });
    }, [sensors]);

    const toggleSeries = useCallback((seriesName: string) => {
        chartInstance.current?.dispatchAction({
            type: "legendToggleSelect",
            name: seriesName,
        });

        setSelectedSeries(prev => ({
            ...prev,
            [seriesName]: !(prev[seriesName] ?? true),
        }));
    }, []);

    const option = useMemo<ECBasicOption>(() => {
        return {
            animation: false,
            hoverLayerThreshold: Infinity,
            stateAnimation: {
                duration: 0,
            },
            color: Object.values(sensorConfig).map(({ color }) => color || ""),
            dataZoom: [
                {
                    type: "inside",
                    xAxisIndex: 0,
                    filterMode: "none",
                },
                {
                    type: "slider",
                    xAxisIndex: 0,
                    height: 20,
                    bottom: 10,
                    labelFormatter: (value: number) => {
                        return datetime(value).format(SHORT_DATETIME_FORMAT);
                    },
                },
            ],
            tooltip: {
                trigger: "axis",
                renderMode: "html",
                appendTo: document.body,
                confine: false,
                extraCssText: `
                    max-height: 60vh;
                    overflow-y: auto;
                    z-index: 99999;
                    pointer-events: auto;
                `,
                axisPointer: {
                    type: "line",
                    axis: "x",
                    label: {
                        show: false,
                    },
                },
                position: (
                    point: number[],
                    _params: any,
                    dom: HTMLElement,
                    _rect: any,
                    size: any
                ) => {
                    const [mouseX, mouseY] = point;
                    const [tooltipWidth, tooltipHeight] = size.contentSize;
                    const [viewWidth, viewHeight] = size.viewSize;

                    let left = mouseX + 16;
                    let top = mouseY + 16;

                    if (left + tooltipWidth > viewWidth) {
                        left = mouseX - tooltipWidth - 16;
                    }

                    if (top + tooltipHeight > viewHeight) {
                        top = mouseY - tooltipHeight - 16;
                    }

                    return {
                        left: Math.max(8, left),
                        top: Math.max(8, top),
                    };
                },
                formatter: (params: any) => {
                    const list = Array.isArray(params) ? params : [params];
                    const time = list[0]?.value?.[0] || list[0]?.axisValue;

                    const rows = list.map((item: any) => {
                        const value = item.value?.[1] ?? item.value;
                        const sensor = sensors.find(
                            ({ name }) => name === item.seriesName
                        );
                        const unit = sensor?.unit ? ` ${sensor.unit}` : "";

                        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:18px;min-width:220px;height:24px;">
                                <span style="display:flex;align-items:center;color:#475467;">
                                    ${sensor ? tooltipMarker(sensor.type, item.seriesName) : ""}
                                    ${item.seriesName}
                                </span>
                                <strong>${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2, })}${unit}</strong>
                            </div>`;
                    }).join("");

                    return `<div style="padding:4px 2px;">
                                <div style="color:#667085;margin-bottom:8px;">${formatTooltipTime(Number(time))}</div>
                                ${rows}
                            </div>`;
                },
            },
            legend: {
                show: false,
                selected: selectedSeries,
            },
            grid: {
                top: 50,
                left: 200,
                right: 80,
                bottom: 64,
            },
            xAxis: [
                {
                    type: "time",
                    axisTick: {
                        show: false,
                    },
                    axisLabel: {
                        color: "#667085",
                        formatter: (value: number) => formatTime(value, showDateOnXAxis),
                        margin: 18,
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#c8d4f8",
                        },
                    },
                    splitLine: {
                        show: false,
                    },
                },
            ],
            yAxis: UNIT_AXES.map(item => unitAxis(item)),
            series: sensors.reduce<any[]>((list, sensor) => {
                const renderer = sensorSeriesRenderers[sensor.type];

                if (!renderer) return list

                return [
                    ...list,
                    ...renderer(convertData(sensor.data, start, finish), sensor.unit, sensor),
                ];
            }, []),
        };
    }, [selectedSeries, sensors, showDateOnXAxis, start, finish]);

    useEffect(() => {
        const el = chartRef.current;
        if (!el) return;

        chartInstance.current = echarts.init(el);

        const handleResize = () => chartInstance.current?.resize();
        const resizeObserver = new ResizeObserver(() => {
            if (el.clientWidth > 0 && el.clientHeight > 0) {
                handleResize();
            }
        });
        resizeObserver.observe(el);
        window.addEventListener("resize", handleResize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", handleResize);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    useEffect(() => {
        if (!chartInstance.current) return;

        chartInstance.current.setOption(option, {
            replaceMerge: ["series", "yAxis"],
        });
    }, [option]);


    return (
        <div>
            <div className="text-[20px] mb-1">
                Tank {firstLetter(name.replace(/_/g, " ").trim())}
            </div>

            <div className={styles.tankChart}>
                <div ref={chartRef} className={styles.chart} />

                <div className={styles.legend}>
                    {sensors.map(sensor => {
                        const active = selectedSeries[sensor.name] ?? true;

                        return (
                            <div
                                key={sensor.name}
                                className={styles.legendItem}
                                data-active={active}
                                onClick={() => toggleSeries(sensor.name)}
                            >
                                <LegendMarker sensor={sensor} />

                                <span>{sensor.name}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
